import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { sammelfallAPI } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  TreeDeciduous, Users, Plus, Loader2, ArrowLeft, Trash2,
  Layers, Check, ArrowRight, Download,
  Axe, Landmark, FileText, Send, Megaphone, Clock, Mail, CheckCircle2,
} from 'lucide-react';

const POTENZIAL_CONFIG = {
  hoch: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', label: 'sammelfall.potenzialHoch' },
  mittel: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', label: 'sammelfall.potenzialMittel' },
  einzeln: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600', label: 'sammelfall.potenzialEinzeln' },
  gering: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600', label: 'sammelfall.potenzialGering' },
};

function ThemenBadges({ fall, t }) {
  if (!fall.harvester_relevant && !fall.foerderung_relevant) return null;
  return (
    <div className="flex gap-1.5 flex-wrap">
      {fall.harvester_relevant && (
        <Badge variant="outline" className="text-[10px] border-orange-300 text-orange-700 bg-orange-50 gap-1" data-testid={`badge-harvester-${fall.fall_index}`}>
          <Axe className="h-3 w-3" /> {t('sammelfall.harvester')}
        </Badge>
      )}
      {fall.foerderung_relevant && (
        <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700 bg-blue-50 gap-1" data-testid={`badge-foerderung-${fall.fall_index}`}>
          <Landmark className="h-3 w-3" /> {t('sammelfall.foerderung')}
        </Badge>
      )}
    </div>
  );
}

function FallKarte({ fall, onEntfernen, index, t }) {
  return (
    <Card className="border-warm-200" data-testid={`fall-karte-${index}`}>
      <CardContent className="pt-5 pb-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge variant="outline" className="text-xs border-primary/30 text-primary bg-primary/5">
                {fall.hauptthema?.label || t('sammelfall.unbekannt')}
              </Badge>
              {fall.merkmale?.slice(0, 3).map((m, i) => (
                <Badge key={i} variant="outline" className="text-xs border-warm-200 text-muted-foreground">
                  {m.label}: {m.wert}
                </Badge>
              ))}
            </div>
            <ThemenBadges fall={fall} t={t} />
            <p className="text-sm text-foreground leading-relaxed mt-1.5" data-testid={`fall-zusammenfassung-${index}`}>
              {fall.zusammenfassung || fall.freitext}
            </p>
            {fall.empfohlener_weg && (
              <p className="text-xs text-muted-foreground mt-2">
                {t('sammelfall.empfehlung')}: {fall.empfohlener_weg}
              </p>
            )}
          </div>
          {onEntfernen && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEntfernen(index)}
              className="shrink-0 text-muted-foreground hover:text-red-500 h-8 w-8 p-0"
              data-testid={`fall-entfernen-${index}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function BuendelungsGruppe({ gruppe, t }) {
  const cfg = POTENZIAL_CONFIG[gruppe.buendelungspotenzial] || POTENZIAL_CONFIG.einzeln;

  // Kostenschätzung
  const kostenProHa = {
    durchforstung: 600, holzernte: 800, aufforstung: 2500,
    kalamitaet: 700, wegebau: 1200, verkehrssicherung: 900,
  }[gruppe.hauptthema] || 750;
  const flaeche = gruppe.flaechen_summe_ha || 0;
  const einzelkosten = flaeche * kostenProHa;
  const hasHarvester = gruppe.harvester_relevant;
  const sparMin = Math.round(einzelkosten * (hasHarvester ? 0.15 : 0.10));
  const sparMax = Math.round(einzelkosten * (hasHarvester ? 0.25 : 0.20));
  const showKosten = flaeche > 0 && gruppe.buendelungspotenzial !== 'einzeln';

  return (
    <div className={`p-4 rounded-lg border ${cfg.bg} ${cfg.border}`} data-testid={`buendelgruppe-${gruppe.hauptthema}`}>
      <div className="flex items-center gap-2 mb-2">
        <Layers className={`h-4 w-4 ${cfg.text}`} />
        <span className={`text-sm font-semibold ${cfg.text}`}>{gruppe.name}</span>
        <Badge variant="outline" className={`text-xs ${cfg.border} ${cfg.text}`}>
          {gruppe.anzahl_faelle} {t('sammelfall.faelle')}
        </Badge>
      </div>
      <p className={`text-xs ${cfg.text} mb-2`}>{t(cfg.label)}</p>
      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
        {gruppe.regionen?.length > 0 && (
          <span>{t('sammelfall.regionen')}: {gruppe.regionen.join(', ')}</span>
        )}
        {gruppe.baumarten?.length > 0 && (
          <span>{t('sammelfall.baumarten')}: {gruppe.baumarten.join(', ')}</span>
        )}
        {gruppe.flaechen_summe_ha > 0 && (
          <span>{t('sammelfall.gesamtflaeche')}: {gruppe.flaechen_summe_ha} ha</span>
        )}
      </div>
      {/* Kostenschätzung */}
      {showKosten && (
        <div className="mt-2 p-2 rounded bg-white/60 border border-emerald-100" data-testid={`kosten-${gruppe.hauptthema}`}>
          <p className="text-xs font-medium text-emerald-800">
            {t('sammelfall.einsparung')}: {sparMin.toLocaleString('de-DE')} - {sparMax.toLocaleString('de-DE')} EUR
          </p>
          <p className="text-[10px] text-muted-foreground">
            {t('sammelfall.kostenBasis')} {kostenProHa} EUR/ha | {t('sammelfall.kostenHinweis')}
          </p>
        </div>
      )}
      {/* Harvester / Foerderung Flags */}
      {(gruppe.harvester_relevant || gruppe.foerderung_relevant) && (
        <div className="flex gap-1.5 mt-2 flex-wrap">
          {gruppe.harvester_relevant && (
            <Badge variant="outline" className="text-[10px] border-orange-300 text-orange-700 bg-orange-50 gap-1">
              <Axe className="h-3 w-3" /> {t('sammelfall.harvester')}
            </Badge>
          )}
          {gruppe.foerderung_relevant && (
            <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700 bg-blue-50 gap-1">
              <Landmark className="h-3 w-3" /> {t('sammelfall.foerderung')}
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

function HarvesterHinweis({ t }) {
  return (
    <Card className="border-orange-200 bg-orange-50/40" data-testid="harvester-hinweis">
      <CardContent className="pt-5 pb-5">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-orange-100 shrink-0">
            <Axe className="h-5 w-5 text-orange-700" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-orange-900 mb-1">{t('sammelfall.harvesterTitle')}</h3>
            <p className="text-sm text-orange-800 leading-relaxed">{t('sammelfall.harvesterDesc')}</p>
            <ul className="mt-2 space-y-1">
              {['harvesterP1', 'harvesterP2', 'harvesterP3'].map(k => (
                <li key={k} className="text-xs text-orange-700 flex items-start gap-1.5">
                  <span className="mt-1 shrink-0 w-1 h-1 rounded-full bg-orange-500" />
                  {t(`sammelfall.${k}`)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FoerderungHinweis({ t }) {
  return (
    <Card className="border-blue-200 bg-blue-50/40" data-testid="foerderung-hinweis">
      <CardContent className="pt-5 pb-5">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-blue-100 shrink-0">
            <Landmark className="h-5 w-5 text-blue-700" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-blue-900 mb-1">{t('sammelfall.foerderungTitle')}</h3>
            <p className="text-sm text-blue-800 leading-relaxed">{t('sammelfall.foerderungDesc')}</p>
            <ul className="mt-2 space-y-1">
              {['foerderungP1', 'foerderungP2', 'foerderungP3'].map(k => (
                <li key={k} className="text-xs text-blue-700 flex items-start gap-1.5">
                  <span className="mt-1 shrink-0 w-1 h-1 rounded-full bg-blue-500" />
                  {t(`sammelfall.${k}`)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AusschreibungBlock({ sammelfall, t, onErfolg }) {
  const [showForm, setShowForm] = useState(false);
  const [titel, setTitel] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [kontaktEmail, setKontaktEmail] = useState('');
  const [frist, setFrist] = useState('');
  const [loading, setLoading] = useState(false);
  const [erfolg, setErfolg] = useState(false);

  // Bereits veröffentlicht?
  const bereitsVeroeffentlicht = sammelfall.ausschreibung_status === 'offen';

  if (bereitsVeroeffentlicht) {
    return (
      <Card className="border-emerald-200 bg-emerald-50/50 mt-8" data-testid="ausschreibung-veroeffentlicht">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-center gap-2.5">
            <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
            <div>
              <p className="text-sm font-medium text-emerald-800">{t('ausschreibung.bereitsVeroeffentlicht')}</p>
              <p className="text-xs text-emerald-600 mt-0.5">{t('ausschreibung.dienstleisterSehen')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (erfolg) {
    return (
      <Card className="border-emerald-200 bg-emerald-50/50 mt-8" data-testid="ausschreibung-erfolg">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-center gap-2.5">
            <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
            <div>
              <p className="text-sm font-medium text-emerald-800">{t('ausschreibung.erfolgTitel')}</p>
              <p className="text-xs text-emerald-600 mt-0.5">{t('ausschreibung.erfolgText')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const handleVeroeffentlichen = async () => {
    if (titel.trim().length < 5) return;
    setLoading(true);
    try {
      await sammelfallAPI.ausschreibungErstellen({
        sammelfall_id: sammelfall.sammelfall_id,
        titel: titel.trim(),
        beschreibung: beschreibung.trim(),
        kontakt_email: kontaktEmail.trim() || null,
        frist: frist || null,
        user_id: sammelfall.user_id || null,
      });
      setErfolg(true);
      toast.success(t('ausschreibung.erfolgTitel'));
      if (onErfolg) onErfolg();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('ausschreibung.fehler'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-primary/20 bg-primary/[0.02] mt-8" data-testid="ausschreibung-block">
      <CardContent className="pt-6 pb-6">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <Megaphone className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h3 className="font-serif text-lg font-bold text-foreground">{t('ausschreibung.titel')}</h3>
            <p className="text-xs text-muted-foreground">{t('ausschreibung.beschreibung')}</p>
          </div>
        </div>

        {!showForm ? (
          <Button
            onClick={() => setShowForm(true)}
            variant="outline"
            className="border-primary/30 text-primary hover:bg-primary/5 gap-2"
            data-testid="ausschreibung-start-btn"
          >
            <Megaphone className="h-4 w-4" />
            {t('ausschreibung.veroeffentlichenBtn')}
          </Button>
        ) : (
          <div className="space-y-3 mt-4" data-testid="ausschreibung-formular">
            <div>
              <label className="text-xs text-muted-foreground font-medium block mb-1">{t('ausschreibung.titelLabel')}</label>
              <input
                type="text"
                value={titel}
                onChange={(e) => setTitel(e.target.value)}
                placeholder={t('ausschreibung.titelPlaceholder')}
                className="w-full text-sm px-3 py-2 rounded-lg border border-warm-200 bg-white focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none"
                data-testid="ausschreibung-titel-input"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground font-medium block mb-1">{t('ausschreibung.beschreibungLabel')}</label>
              <Textarea
                value={beschreibung}
                onChange={(e) => setBeschreibung(e.target.value)}
                placeholder={t('ausschreibung.beschreibungPlaceholder')}
                className="min-h-[80px] text-sm resize-none"
                data-testid="ausschreibung-beschreibung-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground font-medium block mb-1">
                  <Mail className="h-3 w-3 inline mr-1" />
                  {t('ausschreibung.emailLabel')}
                </label>
                <input
                  type="email"
                  value={kontaktEmail}
                  onChange={(e) => setKontaktEmail(e.target.value)}
                  placeholder={t('ausschreibung.emailPlaceholder')}
                  className="w-full text-sm px-3 py-2 rounded-lg border border-warm-200 bg-white focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none"
                  data-testid="ausschreibung-email-input"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-medium block mb-1">
                  <Clock className="h-3 w-3 inline mr-1" />
                  {t('ausschreibung.fristLabel')}
                </label>
                <input
                  type="date"
                  value={frist}
                  onChange={(e) => setFrist(e.target.value)}
                  className="w-full text-sm px-3 py-2 rounded-lg border border-warm-200 bg-white focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none"
                  data-testid="ausschreibung-frist-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <Button
                onClick={handleVeroeffentlichen}
                disabled={loading || titel.trim().length < 5}
                className="gap-2"
                data-testid="ausschreibung-absenden-btn"
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> {t('ausschreibung.wirdVeroeffentlicht')}</>
                ) : (
                  <><Send className="h-4 w-4" /> {t('ausschreibung.jetzt')}</>
                )}
              </Button>
              <Button variant="ghost" onClick={() => setShowForm(false)}>
                {t('sammelfall.abbrechen')}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


export default function SammelfallPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [sammelfall, setSammelfall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [neuerFreitext, setNeuerFreitext] = useState('');

  const laden = useCallback(async () => {
    try {
      const res = await sammelfallAPI.abrufen(id);
      setSammelfall(res.data);
    } catch {
      toast.error(t('sammelfall.ladeFehler'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => { laden(); }, [laden]);

  const fallHinzufuegen = async () => {
    if (!neuerFreitext || neuerFreitext.trim().length < 10) {
      toast.error(t('sammelfall.eingabeFehler'));
      return;
    }
    setAdding(true);
    try {
      const res = await sammelfallAPI.fallHinzufuegen(id, neuerFreitext.trim());
      if (res.data.status === 'ok') {
        toast.success(t('sammelfall.hinzugefuegt'));
        setNeuerFreitext('');
        setShowForm(false);
        await laden();
      } else {
        toast.error(t('sammelfall.analysiertFehler'));
      }
    } catch {
      toast.error(t('sammelfall.analysiertFehler'));
    } finally {
      setAdding(false);
    }
  };

  const fallEntfernen = async (index) => {
    try {
      await sammelfallAPI.fallEntfernen(id, index);
      toast.success(t('sammelfall.entfernt'));
      await laden();
    } catch {
      toast.error(t('sammelfall.entfernenFehler'));
    }
  };

  const handlePdfDownload = () => {
    sammelfallAPI.pdfHerunterladen(id);
    toast.success(t('sammelfall.pdfGestartet'));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-warm-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!sammelfall) {
    return (
      <div className="min-h-screen bg-warm-50 flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">{t('sammelfall.nichtGefunden')}</p>
        <Button variant="outline" onClick={() => navigate('/schnell-check')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> {t('sammelfall.zurueckSchnellCheck')}
        </Button>
      </div>
    );
  }

  const hasBuendelgruppen = sammelfall.buendelung?.gruppen?.some(g => g.buendelungspotenzial !== 'einzeln');
  const hasHarvester = sammelfall.buendelung?.harvester_faelle > 0 || sammelfall.faelle?.some(f => f.harvester_relevant);
  const hasFoerderung = sammelfall.buendelung?.foerderung_faelle > 0 || sammelfall.faelle?.some(f => f.foerderung_relevant);
  const maxFaelle = sammelfall.max_faelle || 3;
  const istVoll = sammelfall.anzahl_faelle >= maxFaelle;

  return (
    <div className="min-h-screen bg-warm-50">
      <header className="border-b border-warm-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-primary font-bold" data-testid="logo-link">
            <TreeDeciduous className="h-5 w-5" />
            <span className="font-serif text-lg">ForestHub</span>
          </Link>
          <Link
            to="/anmelden"
            className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            {t('sammelfall.anmelden')}
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/schnell-check')}
          className="mb-6 text-muted-foreground hover:text-primary"
          data-testid="zurueck-schnellcheck"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" /> {t('sammelfall.zurueckSchnellCheck')}
        </Button>

        {/* FBG Sammelfall Header – unmissverständlich */}
        <div className="mb-8 rounded-xl border-2 border-blue-300 bg-gradient-to-r from-blue-50 to-white p-6" data-testid="sammelfall-header">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center shrink-0">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="font-serif text-2xl sm:text-3xl font-bold text-blue-900" data-testid="sammelfall-titel">
                  {t('sammelfall.titel')}
                </h1>
                <Badge className="bg-blue-600 text-white text-sm px-3 py-1" data-testid="sammelfall-preis">
                  49 EUR
                </Badge>
              </div>
              <p className="text-blue-700 text-sm sm:text-base mt-1 leading-relaxed">
                {t('sammelfall.beschreibung')}
              </p>
            </div>
          </div>

          {/* Fortschrittsanzeige: X/3 Fälle */}
          <div className="mt-4 ml-16" data-testid="sammelfall-progress">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-semibold text-blue-800">
                {sammelfall.anzahl_faelle}/{maxFaelle} {t('sammelfall.faelleErfasst')}
              </span>
              {istVoll && (
                <Badge variant="outline" className="text-xs border-amber-300 text-amber-700 bg-amber-50">
                  {t('sammelfall.limitErreicht')}
                </Badge>
              )}
            </div>
            <div className="h-2.5 bg-blue-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${istVoll ? 'bg-amber-500' : 'bg-blue-600'}`}
                style={{ width: `${(sammelfall.anzahl_faelle / maxFaelle) * 100}%` }}
              />
            </div>
            <p className="text-xs text-blue-600 mt-1">
              {t('sammelfall.preisHinweis')}
            </p>
          </div>
        </div>

        {/* Buendelungs-Uebersicht */}
        {sammelfall.anzahl_faelle >= 2 && sammelfall.buendelung && (
          <Card className="border-primary/20 shadow-soft mb-8" data-testid="buendelung-uebersicht">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Layers className="h-5 w-5 text-primary" />
                  <h2 className="font-serif text-lg font-bold text-foreground">{t('sammelfall.buendelungsanalyse')}</h2>
                </div>
                {/* PDF Download */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePdfDownload}
                  className="h-8 text-xs border-primary/30 text-primary hover:bg-primary/5 gap-1.5"
                  data-testid="sammelfall-pdf-btn"
                >
                  <Download className="h-3.5 w-3.5" />
                  {t('sammelfall.pdfDownload')}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mb-4" data-testid="buendelung-zusammenfassung">
                {sammelfall.buendelung.zusammenfassung}
              </p>
              <div className="space-y-3">
                {sammelfall.buendelung.gruppen.map((g, i) => (
                  <BuendelungsGruppe key={i} gruppe={g} t={t} />
                ))}
              </div>
              {hasBuendelgruppen && (
                <div className="mt-4 p-3 rounded-md bg-primary/[0.04] border border-primary/10">
                  <p className="text-sm text-foreground">
                    {t('sammelfall.buendelHinweis')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Harvester & Foerderung Hinweise */}
        {(hasHarvester || hasFoerderung) && sammelfall.anzahl_faelle >= 1 && (
          <div className="space-y-4 mb-8" data-testid="themen-hinweise">
            {hasHarvester && <HarvesterHinweis t={t} />}
            {hasFoerderung && <FoerderungHinweis t={t} />}
          </div>
        )}

        {/* PDF-Download Button (auch wenn < 2 Faelle, aber >= 1) */}
        {sammelfall.anzahl_faelle >= 1 && !(sammelfall.anzahl_faelle >= 2 && sammelfall.buendelung) && (
          <div className="mb-6">
            <Button
              variant="outline"
              onClick={handlePdfDownload}
              className="border-primary/30 text-primary hover:bg-primary/5 gap-2"
              data-testid="sammelfall-pdf-btn-solo"
            >
              <FileText className="h-4 w-4" />
              {t('sammelfall.pdfDownload')}
            </Button>
          </div>
        )}

        {/* Erfasste Faelle */}
        <div className="mb-8">
          <h2 className="font-serif text-lg font-bold text-foreground mb-4">
            {t('sammelfall.erfassteFaelle')}
          </h2>
          {sammelfall.faelle?.length > 0 ? (
            <div className="space-y-3" data-testid="faelle-liste">
              {sammelfall.faelle.map((f, i) => (
                <FallKarte key={i} fall={f} index={i} onEntfernen={fallEntfernen} t={t} />
              ))}
            </div>
          ) : (
            <div className="p-6 rounded-lg border border-dashed border-warm-300 text-center" data-testid="keine-faelle">
              <p className="text-sm text-muted-foreground mb-2">{t('sammelfall.keineFaelle')}</p>
              <p className="text-xs text-muted-foreground">
                {t('sammelfall.keineFaelleHinweis')}
              </p>
            </div>
          )}
        </div>

        {/* Fall hinzufuegen – nur wenn Limit nicht erreicht */}
        {!istVoll ? (
          showForm ? (
            <Card className="border-blue-200 shadow-soft mb-6" data-testid="fall-hinzufuegen-form">
              <CardContent className="pt-6">
                <p className="text-sm font-medium text-foreground mb-3">
                  {t('sammelfall.neuerFall')}
                </p>
                <Textarea
                  value={neuerFreitext}
                  onChange={(e) => setNeuerFreitext(e.target.value)}
                  placeholder={t('sammelfall.fallPlaceholder')}
                  className="min-h-[120px] text-sm resize-none mb-4"
                  data-testid="neuer-fall-input"
                />
                <div className="flex items-center gap-3">
                  <Button
                    onClick={fallHinzufuegen}
                    disabled={adding || neuerFreitext.trim().length < 10}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="fall-absenden-btn"
                  >
                    {adding ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('sammelfall.wirdAnalysiert')}</>
                    ) : (
                      <><Check className="h-4 w-4 mr-2" /> {t('sammelfall.fallHinzufuegen')}</>
                    )}
                  </Button>
                  <Button variant="ghost" onClick={() => { setShowForm(false); setNeuerFreitext(''); }}>
                    {t('sammelfall.abbrechen')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Button
              variant="outline"
              onClick={() => setShowForm(true)}
              className="w-full sm:w-auto mb-6 border-blue-300 text-blue-700 hover:bg-blue-50"
              data-testid="fall-hinzufuegen-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> {t('sammelfall.weitererFall')} ({sammelfall.anzahl_faelle}/{maxFaelle})
            </Button>
          )
        ) : (
          <div className="mb-6 p-4 rounded-lg border-2 border-amber-300 bg-amber-50 text-center" data-testid="limit-erreicht-hinweis">
            <p className="text-sm font-semibold text-amber-800">
              {t('sammelfall.limitErreichtText')}
            </p>
            <p className="text-xs text-amber-700 mt-1">
              {t('sammelfall.limitErreichtHint')}
            </p>
          </div>
        )}

        {/* CTA: Weiter zur Registrierung */}
        {sammelfall.anzahl_faelle >= 2 && hasBuendelgruppen && (
          <Card className="border-primary/20 bg-primary/[0.03] mt-8" data-testid="sammelfall-cta">
            <CardContent className="pt-6 pb-6">
              <h3 className="font-serif text-lg font-bold text-foreground mb-2">
                {t('sammelfall.ctaTitel')}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {t('sammelfall.ctaBeschreibung')}
              </p>
              <Button
                onClick={() => navigate('/registrieren')}
                className="h-11 px-6"
                data-testid="sammelfall-registrieren-btn"
              >
                {t('sammelfall.ctaButton')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Ausschreibung erstellen */}
        {sammelfall.anzahl_faelle >= 1 && (
          <AusschreibungBlock
            sammelfall={sammelfall}
            t={t}
            onErfolg={() => laden()}
          />
        )}
      </main>

      <footer className="border-t border-warm-200 bg-white mt-auto">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between text-xs text-muted-foreground">
          <span>{t('sammelfall.footerTagline')}</span>
          <div className="flex gap-4">
            <Link to="/datenschutz" className="hover:text-foreground transition-colors">{t('sammelfall.datenschutz')}</Link>
            <Link to="/impressum" className="hover:text-foreground transition-colors">{t('sammelfall.impressum')}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
